import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, ReactNode, Ref } from 'react'
import { forwardRef } from 'react'

type BaseProps = {
  angular: boolean
  /** 如 aspect-[3/4] */
  aspectClass?: string
  className?: string
  /** 未有成图时显示全局骨架 shimmer */
  loading?: boolean
  selected?: boolean
  style?: CSSProperties
  children?: ReactNode
}

type DivProps = BaseProps &
  Pick<HTMLAttributes<HTMLDivElement>, 'onClick'> & {
    as?: 'div'
  }

type ButtonProps = BaseProps &
  Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'aria-label' | 'type'> & {
    as: 'button'
  }

export type ThemedImagePlaceholderProps = DivProps | ButtonProps

function frameRound(angular: boolean): string {
  return angular ? 'rounded-none' : 'rounded-2xl'
}

function frameClass(angular: boolean, aspectClass: string, selected: boolean, extra: string): string {
  const round = frameRound(angular)
  return [
    'relative isolate min-w-0 overflow-hidden border shadow-card',
    selected
      ? 'border-accent/65 ring-2 ring-inset ring-accent/30'
      : 'border-line ring-1 ring-inset ring-line/35',
    'bg-[color-mix(in_srgb,var(--surface-muted)_78%,var(--panel)_22%)]',
    aspectClass,
    round,
    extra,
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * 图片预览/占位外框：与图库 {@link LibraryMasonrySkeletonBlock}、Lightbox、时间线占位共用
 * `glyphicai-skeleton-shimmer` 与主题描边，避免漫画等场景单独写一套颜色。
 */
export const ThemedImagePlaceholder = forwardRef<
  HTMLButtonElement | HTMLDivElement,
  ThemedImagePlaceholderProps
>(function ThemedImagePlaceholder(props, ref) {
  const {
    angular,
    aspectClass = '',
    className = '',
    loading = false,
    selected = false,
    style,
    children,
  } = props
  const round = frameRound(angular)

  const shimmer = loading ? (
    <div
      className={[
        'glyphicai-skeleton-shimmer glyphicai-skeleton-card-pulse absolute inset-0 z-0 ring-1 ring-inset ring-line/45',
        round,
      ].join(' ')}
      aria-hidden
    />
  ) : null

  const content = (
    <>
      {shimmer}
      {children}
    </>
  )

  if (props.as === 'button') {
    const { onClick, 'aria-label': ariaLabel, type = 'button' } = props
    return (
      <button
        ref={ref as Ref<HTMLButtonElement>}
        type={type}
        onClick={onClick}
        aria-label={ariaLabel}
        style={style}
        className={[
          frameClass(angular, aspectClass, selected, className),
          'text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50',
        ].join(' ')}
      >
        {content}
      </button>
    )
  }

  return (
    <div
      ref={ref as Ref<HTMLDivElement>}
      style={style}
      className={frameClass(angular, aspectClass, selected, className)}
    >
      {content}
    </div>
  )
})
